/**
 * Única fuente de verdad para los slugs de path de las tabs/sub-vistas de cada módulo.
 * Ver `.claude/commands/routing.md` para la convención completa — toda tab nueva con slug
 * propio debe sumar su entrada acá + su <Route> en AppRoutes.tsx, y leerse vía `useParams()`.
 */

// ─── Agenda (/calendar) ──────────────────────────────────────────────────────
export type CalendarViewValue = "week" | "month" | "day";

const CALENDAR_VIEW_TO_SLUG: Record<CalendarViewValue, string> = {
  week: "semanal",
  month: "mensual",
  day: "diaria",
};

export const CALENDAR_VIEW_FROM_SLUG: Record<string, CalendarViewValue> = {
  semanal: "week",
  mensual: "month",
  diaria: "day",
};

/** URL canónica de una vista de Agenda. `week` siempre devuelve el slug explícito
 *  (`/calendar/semanal`), nunca el bare `/calendar` — para que forzar "semanal" desde
 *  un botón de navegación no herede la vista guardada en localStorage. */
export const calendarPath = (view: CalendarViewValue): string =>
  `/calendar/${CALENDAR_VIEW_TO_SLUG[view]}`;

// ─── Pacientes (/patients) ───────────────────────────────────────────────────
export type PatientsStatusValue =
  | "all"
  | "lead"
  | "active"
  | "on_hold"
  | "inactive"
  | "transferred";

const PATIENTS_STATUS_TO_SLUG: Record<Exclude<PatientsStatusValue, "all">, string> = {
  lead: "nuevos",
  active: "activos",
  on_hold: "en-pausa",
  inactive: "inactivos",
  transferred: "derivacion-exitosa",
};

export const PATIENTS_STATUS_FROM_SLUG: Record<string, PatientsStatusValue> = {
  nuevos: "lead",
  activos: "active",
  "en-pausa": "on_hold",
  inactivos: "inactive",
  "derivacion-exitosa": "transferred",
};

/** `all` es el default: su slug directo es el bare `/patients`. */
export const patientsPath = (status: PatientsStatusValue): string =>
  status === "all" ? "/patients" : `/patients/${PATIENTS_STATUS_TO_SLUG[status]}`;

// ─── Perfil (/profile) ───────────────────────────────────────────────────────
export type ProfileTabValue =
  | "general"
  | "attention"
  | "vacations"
  | "metrics"
  | "therapeuticStyle"
  // Solo en la app mobile (PWA): las preferencias de notificaciones del dispositivo viven dentro de Perfil.
  | "notifications";

const PROFILE_TAB_TO_SLUG: Record<Exclude<ProfileTabValue, "general">, string> = {
  attention: "atencion",
  vacations: "vacaciones",
  metrics: "metricas",
  // Sub-tab de rollout controlado (BetaTestersService) — ver TherapeuticStyleTab.
  therapeuticStyle: "estilo-terapeutico",
  notifications: "notificaciones",
};

export const PROFILE_TAB_FROM_SLUG: Record<string, ProfileTabValue> = {
  atencion: "attention",
  vacaciones: "vacations",
  metricas: "metrics",
  "estilo-terapeutico": "therapeuticStyle",
};

/**
 * Tabs de Perfil que existen SOLO en la app mobile (PWA). Van aparte de `PROFILE_TAB_FROM_SLUG` a propósito: ese mapa es el
 * de las rutas del router web (el test de cobertura de Métricas de Actividad exige una pantalla del catálogo por cada una), y
 * la app mobile no corre el tracker de actividad. En la web este slug no abre nada.
 */
export const PWA_PROFILE_TAB_FROM_SLUG: Record<string, ProfileTabValue> = {
  notificaciones: "notifications",
};

/** `general` es el default: su slug directo es el bare `/profile`. */
export const profilePath = (tab: ProfileTabValue): string =>
  tab === "general" ? "/profile" : `/profile/${PROFILE_TAB_TO_SLUG[tab]}`;

// ─── Perfil → Métricas (/profile/metricas) ──────────────────────────────────
export type MetricsSubTabValue =
  | "resumen"
  | "inactividad"
  | "asignacion"
  | "asignacion-inicial"
  | "psi-plus"
  | "liquidacion";

// Los ids internos ya son kebab-case y coinciden 1:1 con el slug — no hace falta traducir,
// solo distinguir el default ("resumen", que vive en el bare `/profile/metricas`).
// "camino-inicial" se mudó a la tab de nivel superior `/camino-inicial` (ver CaminoInicialPage) —
// ya no es una sub-tab de Métricas.
export const METRICS_SUBTABS_WITH_SLUG: Exclude<MetricsSubTabValue, "resumen">[] = [
  "inactividad",
  "asignacion",
  "asignacion-inicial",
  "psi-plus",
  "liquidacion",
];

export const isMetricsSubTabSlug = (value: string): value is Exclude<MetricsSubTabValue, "resumen"> =>
  (METRICS_SUBTABS_WITH_SLUG as string[]).includes(value);

/** `resumen` es el default: su slug directo es el bare `/profile/metricas`. */
export const metricsPath = (subtab: MetricsSubTabValue): string =>
  subtab === "resumen" ? "/profile/metricas" : `/profile/metricas/${subtab}`;

// ─── Perfil → Métricas → secciones internas (/profile/metricas/<subtab>/<seccion>) ──────────────
// Liquidación y Programa PSI+ tienen sub-pestañas propias. El default de cada una vive en el path del sub-tab
// pelado (ej. `/profile/metricas/liquidacion` = la lista de liquidaciones); las demás agregan su slug.
export const METRICS_SECTIONS = {
  liquidacion: { default: "liquidaciones", slugs: ["info", "pago"] },
  "psi-plus": { default: "detalles", slugs: ["bono-500", "bonos-1000-2000", "historial"] },
} as const;

export type MetricsSectionParent = keyof typeof METRICS_SECTIONS;

// Paneles laterales ("Repasar conceptos", glosarios) de Asignación, Inactividad y Asignación inicial. A diferencia de
// las secciones, el default es "sin panel abierto" (path pelado del sub-tab) y el slug es el id interno del panel.
export const METRICS_PANELS = {
  asignacion: ["hub", "concept", "blocks", "cupo", "horarios", "reprogramacion", "inactividad"],
  inactividad: ["proceso", "porque", "concordancia", "exclusiones", "universo", "bajas", "excluidos", "todos", "objetivo"],
  "asignacion-inicial": ["proceso", "adherencia", "horarios"],
} as const;

export type MetricsPanelParent = keyof typeof METRICS_PANELS;

/** ¿`value` es el slug de un panel de `parent`? */
export const isMetricsPanelSlug = (parent: string, value: string): boolean =>
  parent in METRICS_PANELS && (METRICS_PANELS[parent as MetricsPanelParent] as readonly string[]).includes(value);

/** Path de un panel abierto, o el del sub-tab pelado si no hay ninguno. */
export const metricsPanelPath = (parent: MetricsPanelParent, panel: string | null): string =>
  panel ? `${metricsPath(parent)}/${panel}` : metricsPath(parent);

/** ¿`value` es el slug de una sección NO default o de un panel de `parent`? (el default no lleva slug). */
export const isMetricsSectionSlug = (parent: string, value: string): boolean =>
  (parent in METRICS_SECTIONS && (METRICS_SECTIONS[parent as MetricsSectionParent].slugs as readonly string[]).includes(value)) ||
  isMetricsPanelSlug(parent, value);

/** Path de una sección: el default es el path pelado del sub-tab; el resto suma su slug. */
export const metricsSectionPath = (parent: MetricsSectionParent, section: string): string =>
  section === METRICS_SECTIONS[parent].default ? metricsPath(parent) : `${metricsPath(parent)}/${section}`;
